import { Box, FormControl, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, Typography, useMediaQuery } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getLogOverview, LogOverview as Overview } from "../../service/LogService";
import { formatTime, StyledTableCell, StyledTableRow, TableWrapper } from "./logStyles";

const PERIODS = [1, 24, 168];
const REFRESH_INTERVAL_MS = 30000;

const rate = (value: number | null) => (value === null ? "-" : `${value} %`);

/** Key figures in tables (the design has no charts) */
export default function LogOverview() {
  const [t] = useTranslation("global");
  const isMobile = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();
  const [hours, setHours] = useState(24);
  const [data, setData] = useState<Overview | null>(null);

  useEffect(() => {
    const load = () => getLogOverview(hours, isMobile, navigate).then(setData);
    load();
    const interval = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours]);

  const figures: [string, string | number][] = data
    ? [
        [t("logs.figErrors"), data.levels.error],
        [t("logs.figWarnings"), data.levels.warn],
        [t("logs.figRequests"), data.http.requests],
        [t("logs.figDenied"), data.http.denied],
        [t("logs.figRateLimited"), data.http.rateLimited],
        [t("logs.figLoginOk"), data.security.loginSucceeded],
        [t("logs.figLoginFailed"), data.security.loginFailed],
        [t("logs.figPassword"), data.security.passwordChanged],
        [t("logs.figSms"), `${data.sms.sent} / ${data.sms.failed}`],
        [t("logs.figSmsRate"), rate(data.sms.deliveryRate)],
        [t("logs.figEmail"), `${data.email.sent} / ${data.email.failed}`],
        [t("logs.figEmailRate"), rate(data.email.deliveryRate)],
        [t("logs.figCron"), `${data.cron.finished} / ${data.cron.failed}`],
        [t("logs.figStripe"), `${data.stripe.received} / ${data.stripe.failed} / ${data.stripe.rejected}`],
        [t("logs.figClient"), data.client.errors],
      ]
    : [];

  return (
    <>
      <Box sx={{ display: "flex", mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t("logs.period")}</InputLabel>
          <Select label={t("logs.period")} value={hours} onChange={(event) => setHours(Number(event.target.value))}>
            {PERIODS.map((period) => (
              <MenuItem key={period} value={period}>
                {t(`logs.hours${period}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TableWrapper component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t("logs.keyFigures")}</StyledTableCell>
              <StyledTableCell align="right">{t("logs.value")}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {figures.map(([label, value]) => (
              <StyledTableRow key={label}>
                <TableCell>{label}</TableCell>
                <TableCell align="right">{value}</TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableWrapper>

      <Typography variant="h6" sx={{ width: "100%", mt: 2 }}>
        {t("logs.alerts")}
      </Typography>
      <TableWrapper component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t("logs.time")}</StyledTableCell>
              <StyledTableCell>{t("logs.event")}</StyledTableCell>
              <StyledTableCell>{t("logs.message")}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.alerts || []).map((row) => (
              <StyledTableRow key={row.id}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{formatTime(row.createdAt)}</TableCell>
                <TableCell>{row.event}</TableCell>
                <TableCell>{row.message}</TableCell>
              </StyledTableRow>
            ))}
            {!data?.alerts.length && (
              <TableRow>
                <TableCell colSpan={3} sx={{ textAlign: "center" }}>
                  {t("logs.none")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      <Typography variant="h6" sx={{ width: "100%", mt: 2 }}>
        {t("logs.failedCron")}
      </Typography>
      <TableWrapper component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t("logs.time")}</StyledTableCell>
              <StyledTableCell>{t("logs.message")}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.cron.latestFailures || []).map((row) => (
              <StyledTableRow key={row.id}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{formatTime(row.createdAt)}</TableCell>
                <TableCell>{row.message}</TableCell>
              </StyledTableRow>
            ))}
            {!data?.cron.latestFailures.length && (
              <TableRow>
                <TableCell colSpan={2} sx={{ textAlign: "center" }}>
                  {t("logs.none")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      <Typography variant="h6" sx={{ width: "100%", mt: 2 }}>
        {t("logs.perHour")}
      </Typography>
      <TableWrapper component={Paper} sx={{ marginBottom: "2%" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t("logs.hour")}</StyledTableCell>
              <StyledTableCell align="right">{t("logs.figErrors")}</StyledTableCell>
              <StyledTableCell align="right">{t("logs.figWarnings")}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.perHour || []).map((row) => (
              <StyledTableRow key={row.hour}>
                <TableCell>{formatTime(row.hour)}</TableCell>
                <TableCell align="right">{row.errors}</TableCell>
                <TableCell align="right">{row.warnings}</TableCell>
              </StyledTableRow>
            ))}
            {!data?.perHour.length && (
              <TableRow>
                <TableCell colSpan={3} sx={{ textAlign: "center" }}>
                  {t("logs.none")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>
    </>
  );
}
